import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, SimpleChange } from '@angular/core'
import { GoogleChartComponent, ChartSelectionChangedEvent } from 'angular-google-charts'
import { Observable, Subscription, of } from 'rxjs'
import { lineChartStyleDefault } from 'src/app/utils/line-chart-style'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/reducers'
import { getTiktok, getLoading, getTiktokCsv } from 'src/app/selectors/tiktok-graph.selector'
import { map, take } from 'rxjs/operators'
import { isEventDrawerOpened } from 'src/app/selectors/shell.selector'
import { getSelectedEventDate } from 'src/app/selectors/event.selector'
import * as moment from 'moment'
import { openEventDrawer } from 'src/app/actions/shell.actions'
import { selectEventDate } from 'src/app/actions/event.actions'
import { range } from 'src/app/utils/range'
import {
  getDataCollectByTiktokGraph,
  getDataCollectLoading,
} from 'src/app/selectors/data-collections.selector'
import { TiktokGraphPoint } from 'src/app/models/tiktok-graph.model'
import { Artist } from 'src/app/models/artist.model'
import { getSelectedArtist } from 'src/app/selectors/artist.selector'
import { MediaObserver, MediaChange } from '@angular/flex-layout'
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout'

@Component({
    selector: 'app-tiktok-graph',
    templateUrl: './tiktok-graph.component.html',
    styleUrls: ['./tiktok-graph.component.scss'],
    standalone: false
})
export class TiktokGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chart')
  chart: GoogleChartComponent

  @ViewChild('container')
  container: ElementRef

  private widthOpened = false
  private widthWhenOpened = 0
  private widthWhenClosed = 0

  contentTypes$: Observable<string[]>
  tiktok$: Observable<any[]>
  isLoading$: Observable<boolean>

  dataCollections$: Observable<boolean>
  dataCollectLoading$: Observable<boolean>

  colorsOption: any[]
  tiktokOptions: any = {
    ...lineChartStyleDefault,
  }

  private subscriptions: Subscription[] = []

  csvId = 'tiktok'
  csvData$: Observable<any[]>
  csvFileName = 'TikTok Creation数'
  selectedArtist$: Observable<Artist>

  isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(map(it => it.matches))

  checkedSubAccounts: any[]
  checkLabels: { name: string; completed: boolean; disabled: boolean }[] = []
  allTiktokTypes$: Observable<string[]>

  constructor(
    private store: Store<AppState>,
    public media: MediaObserver,
    private breakpointObserver: BreakpointObserver
  ) {
    this.subscriptions.push(media.asObservable().subscribe(() => {}))
  }

  ngOnInit() {
    this.isLoading$ = this.store.select(getLoading)
    this.getIniData()

    this.dataCollections$ = this.store.select(getDataCollectByTiktokGraph)
    this.dataCollectLoading$ = this.store.select(getDataCollectLoading)
    this.csvData$ = this.store.select(getTiktokCsv)
    this.selectedArtist$ = this.store.select(getSelectedArtist)

    this.subscriptions.push(
      this.store.select(isEventDrawerOpened).subscribe(opened => {
        if (!this.chart) {
          return
        }
        this.widthOpened = opened
        this.chart.width = opened ? this.widthWhenOpened : this.widthWhenClosed
        this.redrawChart()
      })
    )

    this.subscriptions.push(
      this.store.select(getSelectedEventDate).subscribe(date => {
        if (!this.chart) {
          return
        }

        const googleChart = this.chart.chart

        if (!googleChart) {
          return
        }

        if (!date) {
          googleChart.setSelection([])
          return
        }

        const ymd = moment(date).format('YYYY-MM-DD')
        const columnNum = (this.chart.columns || []).length - 1

        this.tiktok$.pipe(take(1)).subscribe(tiktok => {
          const index = tiktok.findIndex(data => data[0] === ymd)

          if (index === -1) {
            return
          }

          const selections: { row: number; column: number }[] = range(
            0,
            columnNum
          ).map(i => ({
            row: index,
            column: i + 1,
          }))

          googleChart.setSelection(selections)
        })
      })
    )

    if (this.media.isActive('lt-md')) {
      this.tiktokOptions.legend.position = 'none'
      this.tiktokOptions.chartArea.left = 45
    } else {
      this.tiktokOptions.legend.position = 'top'
      this.tiktokOptions.chartArea.left = 80
    }
  }

  getIniData() {
    this.allTiktokTypes$ = this.store
      .select(getTiktok)
      .pipe(map(it => [...new Set(it.map(item => item.contentType))]))

    this.contentTypes$ = this.store
      .select(getTiktok)
      .pipe(map(it => ['日付'].concat([...new Set(it.map(item => item.contentType))])))
    this.tiktok$ = this.store.select(getTiktok).pipe(
      map(it => {
        return this.convert2ChartData(it)
      })
    )
    this.subscriptions.push(
      this.allTiktokTypes$.subscribe(contentTypes => {
        this.checkLabels = contentTypes.map(contentType => ({
          name: contentType,
          completed: true,
          disabled: false,
        }))
      })
    )

    // streamsの取得先と色をマッピングして、グラフのオプションを設定する
    this.subscriptions.push(
      this.contentTypes$
        .pipe(
          map(contentTypes => this.mappingSourceAndColor(contentTypes))
        )
        .subscribe(colors => {
          this.colorsOption = [...colors]

          this.tiktokOptions = {
            ...lineChartStyleDefault,
            colors: this.colorsOption,
          }
        })
    )
  }

  // 選択したsubAccountsのstreamsを表示する
  onChange() {
    // 一つのcheckboxだけをチェックしたら、disableする
    this.disableCheckbox()

    // checked subAccountsを取得する
    this.checkedSubAccounts = []
    this.checkLabels.forEach(it => {
      if (it.completed) {
        this.checkedSubAccounts.push(it.name)
      }
    })

    // 選択したsubAccountsを再取得する
    this.contentTypes$ = of(['日付'].concat(this.checkedSubAccounts))

    // 選択したsubAccountsのstreamsを再取得する
    this.tiktok$ = this.store.select(getTiktok).pipe(
      map(it => {
        return this.convertSelected2ChartData(it)
      })
    )

    // 選択したsubAccountsの色を再設定する
    this.contentTypes$
      .pipe(
        map(it => {
          // streamsの取得先と色をマッピング
          return this.mappingSourceAndColor(it)
        })
      )
      .subscribe(val => {
        // 色のオプションを取得する
        this.colorsOption = []
        val.forEach(d => this.colorsOption.push(d))

        // グラフのオプションを設定する
        this.tiktokOptions = {
          ...lineChartStyleDefault,
          colors: this.colorsOption,
        }
      })
  }

  private disableCheckbox() {
    var count = 0
    for (var i = 0; i < this.checkLabels.length; i++) {
      if (this.checkLabels[i].completed) {
        count++

        if (this.checkLabels[i].disabled) {
          this.checkLabels[i].disabled = false
        }
      }
    }
    if (count === 1) {
      for (var i = 0; i < this.checkLabels.length; i++) {
        if (this.checkLabels[i].completed) {
          this.checkLabels[i].disabled = true
        }
      }
    }
  }

  ngAfterViewInit() {
    if (!this.container) {
      return
    }
    this.widthWhenClosed = this.container.nativeElement.offsetWidth - 20
    this.widthWhenOpened = this.widthWhenClosed - 300 /* 右ドロワーの幅に合わせて！ */
  }

  ngOnDestroy() {
    this.subscriptions.forEach(it => it.unsubscribe())
  }

  onResize() {
    if (!this.chart) {
      return
    }
    // リサイズ時、出来事の開閉状態によって算出が異なる
    if (this.widthOpened) {
      this.widthWhenOpened = this.container.nativeElement.offsetWidth - 20
      this.widthWhenClosed = this.widthWhenOpened + 300
      this.chart.width = this.widthWhenOpened
    } else {
      this.widthWhenClosed = this.container.nativeElement.offsetWidth - 20
      this.widthWhenOpened = this.widthWhenClosed - 300
      this.chart.width = this.widthWhenClosed
    }
    this.redrawChart()
  }

  onSelect(event: ChartSelectionChangedEvent): void {
    const selected = event.selection && event.selection[0]

    if (!selected || selected.row == null) {
      return
    }

    const selectedRow = selected.row

    this.tiktok$.pipe(take(1)).subscribe(tiktok => {
      if (!tiktok[selectedRow]) {
        return
      }

      this.isHandset$.pipe(take(1)).subscribe(isHandset => {
        if (isHandset) {
          return
        }

        this.store.dispatch(openEventDrawer())
        this.store.dispatch(
          selectEventDate({
            eventDate: tiktok[selectedRow][0],
          })
        )
      })
    })
  }

  private convertSelected2ChartData(streams: TiktokGraphPoint[]): any[] {
    const dates = [...new Set(streams.map(item => item.streamDate))]
    const column = []
    dates.forEach(d => {
      const data: any[] = [d]
      column.push(data)
      streams.map(stream => {
        if (d === stream.streamDate) {
          this.checkedSubAccounts.forEach(item => {
            if (item === stream.contentType) {
              data.push(stream.streams)
            }
          })
        }
      })
    })
    return column
  }

  private convert2ChartData(tiktok: TiktokGraphPoint[]): any[] {
    const dates = [...new Set(tiktok.map(item => item.streamDate))]
    const column = []
    dates.forEach(d => {
      const data: any[] = [d]
      column.push(data)
      tiktok.map(tiktok => {
        if (d === tiktok.streamDate) {
          data.push(tiktok.streams)
        }
      })
    })
    return column
  }

  /* tiktokのtypeと色をマッピング処理
     tiktokTypeがUGC, PGCの場合、グラフ線の色を固定します。上記以外の3件のtypeの色はランダムします。それ以外の場合は同じ色にします。
  */
  private mappingSourceAndColor(subAccounts: string[]): string[] {
    const colors = []
    const colorsRandom = ['#2196f3', '#ffc107', '#03a9f4', '#009688', '#3f51b5', '#ffeb3b']
    const colors14Above = '#ff22ca'
    subAccounts.forEach(s => {
      switch (s) {
        case 'PGC': {
          colors.push('#e60000')
          break
        }
        case 'UGC': {
          colors.push('#673ab7')
          break
        }
        case '日付': {
          break
        }
        default: {
          if (colorsRandom.length > 0) {
            // 上記以外の3件の取得先が色がランダムします。
            colors.push(colorsRandom.shift())
          } else {
            // 上記以外取得先の色が同じ色にします。
            colors.push(colors14Above)
          }
          break
        }
      }
    })
    return colors
  }

  private redrawChart(): void {
    if (!this.chart) {
      return
    }

    this.chart.ngOnChanges({
      width: new SimpleChange(
        undefined,
        this.chart.width,
        false
      ),
    })
  }
}
